import React, { useState, useEffect } from 'react';
import { X, Trash2, Upload, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { supabase } from '../../lib/supabaseClient';
import SkillsInput from '../ui/skills-input';

const CLEARANCE_LEVELS = ['None', 'Baseline', 'NV1', 'NV2', 'TSPV'];
const CANDIDATE_STATUSES = ['Active', 'On Hold', 'Blacklisted', 'Inactive'];
const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const FILE_TYPES = ['cv', 'cover_letter', 'portfolio', 'certification', 'other'];
const FILE_TYPE_LABELS = {
  cv: 'Resume',
  cover_letter: 'Cover Letter',
  portfolio: 'Portfolio',
  certification: 'Certification',
  other: 'Other',
};
const AUSTRALIAN_STATES = ['ACT', 'NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT'];

const AddCandidateModal = ({ open, onOpenChange, onSuccess, editMode = false, existingCandidate = null }) => {
  const [loading, setLoading] = useState(false);
  const [skills, setSkills] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [availablePositions, setAvailablePositions] = useState([]);
  const [positionSearchTerm, setPositionSearchTerm] = useState('');
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    known_as: '',
    email: '',
    phone: '',
    location_city: '',
    location_state: '',
    location_country: 'Australia',
    clearance_level: '',
    citizenship_status: '',
    payroll_type: '',
    pty_ltd_abn: '',
    position_id: '',
    status: 'Active',
    notes: '',
    onboarding_complete: true,
  });

  // Skills state
  const [candidateSkills, setCandidateSkills] = useState([]);
  const [skillsToAdd, setSkillsToAdd] = useState([]);
  const [skillsToDelete, setSkillsToDelete] = useState([]);

  // Files state
  const [candidateFiles, setCandidateFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});

  // Fetch available skills and positions
  useEffect(() => {
    if (open) {
      fetchSkills();
      fetchPositions();
    }
  }, [open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Prefill form data when in edit mode
  useEffect(() => {
    if (editMode && existingCandidate && open) {
      setFormData({
        first_name: existingCandidate.first_name || '',
        last_name: existingCandidate.last_name || '',
        known_as: existingCandidate.known_as || '',
        email: existingCandidate.email || '',
        phone: existingCandidate.phone || '',
        location_city: existingCandidate.location_city || '',
        location_state: existingCandidate.location_state || '',
        location_country: existingCandidate.location_country || 'Australia',
        clearance_level: existingCandidate.clearance_level || '',
        citizenship_status: existingCandidate.citizenship_status || '',
        payroll_type: existingCandidate.payroll_type || '',
        pty_ltd_abn: existingCandidate.pty_ltd_abn || '',
        position_id: existingCandidate.position_id || '',
        status: existingCandidate.status || 'Active',
        notes: existingCandidate.notes || '',
        onboarding_complete: existingCandidate.onboarding_complete !== false,
      });

      // Load existing skills and files
      if (existingCandidate.id) {
        fetchCandidateSkills(existingCandidate.id);
        fetchCandidateFiles(existingCandidate.id);
      }
    }
  }, [editMode, existingCandidate, open]);

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      known_as: '',
      email: '',
      phone: '',
      location_city: '',
      location_state: '',
      location_country: 'Australia',
      clearance_level: '',
      citizenship_status: '',
      payroll_type: '',
      pty_ltd_abn: '',
      position_id: '',
      status: 'Active',
      notes: '',
      onboarding_complete: true,
    });
    setCandidateSkills([]);
    setCandidateFiles([]);
    setUploadingFiles({});
  };

  const fetchSkills = async () => {
    try {
      const { data, error } = await supabase
        .from('skills_master')
        .select('*')
        .order('name');

      if (error) throw error;
      setAvailableSkills(data || []);
    } catch (err) {
      console.error('Error fetching skills:', err);
    }
  };

  const fetchPositions = async () => {
    try {
      const { data, error } = await supabase
        .from('positions_master')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setAvailablePositions(data || []);
    } catch (err) {
      console.error('Error fetching positions:', err);
    }
  };

  const fetchCandidateSkills = async (candidateId) => {
    try {
      const { data, error } = await supabase
        .from('candidate_skills')
        .select('*, skill:skills_master(*)')
        .eq('candidate_id', candidateId);

      if (error) throw error;
      setCandidateSkills(data || []);
    } catch (err) {
      console.error('Error fetching candidate skills:', err);
    }
  };

  const fetchCandidateFiles = async (candidateId) => {
    try {
      const { data, error } = await supabase
        .from('candidate_files')
        .select('*')
        .eq('candidate_id', candidateId);

      if (error) throw error;
      setCandidateFiles(data || []);
    } catch (err) {
      console.error('Error fetching candidate files:', err);
    }
  };

  // Filter positions based on search term
  const filteredPositions = availablePositions.filter(position =>
    position.name.toLowerCase().includes(positionSearchTerm.toLowerCase())
  );

  // Get selected position for display
  const selectedPosition = availablePositions.find(p => p.id === formData.position_id);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePositionSelect = (positionId) => {
    setFormData((prev) => ({
      ...prev,
      position_id: positionId,
    }));
    setPositionSearchTerm('');
    setShowPositionDropdown(false);
  };

  const handlePositionInputChange = (e) => {
    setPositionSearchTerm(e.target.value);
    setShowPositionDropdown(true);
  };

  const clearPosition = () => {
    setFormData((prev) => ({
      ...prev,
      position_id: '',
    }));
    setPositionSearchTerm('');
  };

  const handleSkillsChange = (newSkills) => {
    setCandidateSkills(newSkills);
  };

  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileId = Date.now().toString();
    setUploadingFiles((prev) => ({ ...prev, [fileId]: true }));

    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `candidate-files/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('candidate-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('candidate-documents')
        .getPublicUrl(filePath);

      // Add to files list
      setCandidateFiles((prev) => [
        ...prev,
        {
          id: fileId,
          file_type: fileType,
          file_name: file.name,
          file_url: urlData.publicUrl,
          file_size: file.size,
        },
      ]);
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload file: ' + err.message);
    } finally {
      setUploadingFiles((prev) => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });
    }
  };

  const handleRemoveFile = async (index, fileId) => {
    // If file has URL, try to delete from storage
    const file = candidateFiles[index];
    if (file.file_url && file.file_url.includes('candidate-documents/')) {
      try {
        const filePath = file.file_url.split('candidate-documents/')[1];
        await supabase.storage.from('candidate-documents').remove([filePath]);
      } catch (err) {
        console.error('Error deleting file from storage:', err);
      }
    }

    setCandidateFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.first_name || !formData.last_name) {
        throw new Error('First name and last name are required');
      }

      // Prepare data for save - convert empty strings to null for UUID fields
      const dataToSave = {
        ...formData,
        position_id: formData.position_id || null,
      };

      let candidateId;

      if (editMode && existingCandidate) {
        // Update existing candidate
        const { error: updateError } = await supabase
          .from('candidates')
          .update(dataToSave)
          .eq('id', existingCandidate.id);

        if (updateError) throw updateError;
        candidateId = existingCandidate.id;

        // Delete existing skills (RLS allows this)
        await supabase.from('candidate_skills').delete().eq('candidate_id', candidateId);

        // Delete existing files via backend (service role bypasses RLS)
        const { data: { session } } = await supabase.auth.getSession();
        const API_URL = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
        await fetch(`${API_URL}/admin/candidates/${candidateId}/files`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
      } else {
        // Create new candidate
        const { data: candidateData, error: candidateError } = await supabase
          .from('candidates')
          .insert([dataToSave])
          .select()
          .single();

        if (candidateError) throw candidateError;
        candidateId = candidateData.id;
      }

      // Insert candidate skills
      if (candidateSkills.length > 0) {
        const skillsToInsert = candidateSkills
          .filter((skill) => skill.skill_id)
          .map((skill) => ({
            candidate_id: candidateId,
            skill_id: skill.skill_id,
            level: skill.level,
            source: skill.source || 'manual',
          }));

        if (skillsToInsert.length > 0) {
          const { error: skillsError } = await supabase
            .from('candidate_skills')
            .insert(skillsToInsert);

          if (skillsError) throw skillsError;
        }
      }

      // Insert candidate files
      if (candidateFiles.length > 0) {
        const filesToInsert = candidateFiles.map((file) => ({
          candidate_id: candidateId,
          file_type: file.file_type,
          file_name: file.file_name,
          file_url: file.file_url,
          file_size: file.file_size,
        }));

        const { error: filesError } = await supabase
          .from('candidate_files')
          .insert(filesToInsert);

        if (filesError) throw filesError;
      }

      onSuccess();
      onOpenChange(false);
    } catch (err) {
      console.error('Error saving candidate:', err);
      alert('Error saving candidate: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
        <form onSubmit={handleSubmit} className="contents">
          {/* Header - sticky at top */}
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Candidate' : 'Add New Candidate'}</DialogTitle>
              <DialogDescription>
                {editMode ? 'Update candidate information' : 'Enter candidate details to add to the system'}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6">


            {/* Basic Information */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="known_as">Known As</Label>
                    <Input
                      id="known_as"
                      name="known_as"
                      value={formData.known_as}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {CANDIDATE_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg">Location</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="location_city">City</Label>
                    <Input
                      id="location_city"
                      name="location_city"
                      value={formData.location_city}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="location_state">State</Label>
                    <select
                      id="location_state"
                      name="location_state"
                      value={formData.location_state}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="">Select state</option>
                      {AUSTRALIAN_STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="location_country">Country</Label>
                    <Input
                      id="location_country"
                      name="location_country"
                      value={formData.location_country}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Security & Employment */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg">Security & Employment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="clearance_level">Clearance Level</Label>
                    <select
                      id="clearance_level"
                      name="clearance_level"
                      value={formData.clearance_level}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="">Select clearance level</option>
                      {CLEARANCE_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="citizenship_status">Citizenship Status</Label>
                    <Input
                      id="citizenship_status"
                      name="citizenship_status"
                      value={formData.citizenship_status}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="relative">
                    <Label htmlFor="position_search">Current Position</Label>
                    <div className="relative">
                      <Input
                        id="position_search"
                        type="text"
                        placeholder={selectedPosition ? selectedPosition.name : "Search positions..."}
                        value={positionSearchTerm}
                        onChange={handlePositionInputChange}
                        onFocus={() => setShowPositionDropdown(true)}
                        className="pr-8"
                      />
                      {selectedPosition && (
                        <button
                          type="button"
                          onClick={clearPosition}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {showPositionDropdown && filteredPositions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredPositions.map((position) => (
                          <div
                            key={position.id}
                            className="px-3 py-2 hover:bg-accent cursor-pointer text-sm"
                            onClick={() => handlePositionSelect(position.id)}
                          >
                            {position.name}
                            {position.category && (
                              <span className="text-xs text-muted-foreground ml-2">({position.category})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="payroll_type">Payroll Type</Label>
                    <Input
                      id="payroll_type"
                      name="payroll_type"
                      value={formData.payroll_type}
                      onChange={handleInputChange}
                      placeholder="e.g., PAYG, PTY LTD"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pty_ltd_abn">PTY LTD ABN</Label>
                    <Input
                      id="pty_ltd_abn"
                      name="pty_ltd_abn"
                      value={formData.pty_ltd_abn}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Skills */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg">Skills</CardTitle>
              </CardHeader>
              <CardContent>
                <SkillsInput
                  skills={candidateSkills.map(cs => ({
                    id: cs.skill_id,
                    name: availableSkills.find(s => s.id === cs.skill_id)?.name || '',
                    level: cs.level,
                    source: cs.source
                  })).filter(s => s.name)}
                  onChange={handleSkillsChange}
                  availableSkills={availableSkills}
                  allowFreeText={false}
                  placeholder="Type to search and add skills..."
                />
              </CardContent>
            </Card>

            {/* Files */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg">Files & Documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {FILE_TYPES.map((fileType) => (
                  <div key={fileType}>
                    <Label>{FILE_TYPE_LABELS[fileType] || fileType}</Label>
                    <div className="flex gap-2 items-center mt-1">
                      <Input
                        type="file"
                        onChange={(e) => handleFileUpload(e, fileType)}
                        className="flex-1"
                      />
                      {uploadingFiles[fileType] && <Loader2 className="h-4 w-4 animate-spin" />}
                    </div>
                    <div className="mt-2 space-y-1">
                      {candidateFiles
                        .filter((f) => f.file_type === fileType)
                        .map((file, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            <span className="flex-1 truncate">{file.file_name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveFile(index, file.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Internal notes about the candidate..."
                />
              </CardContent>
            </Card>

            {/* Onboarding - only show in edit mode */}
            {editMode && (
              <Card className="mb-4">
                <CardContent className="pt-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.onboarding_complete}
                      onChange={(e) => setFormData(prev => ({ ...prev, onboarding_complete: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium">Onboarding Complete</span>
                      <p className="text-xs text-muted-foreground">Uncheck to require this candidate to complete onboarding again on next login</p>
                    </div>
                  </label>
                </CardContent>
              </Card>
            )}

          </div>

          {/* Footer - sticky at bottom */}
          <div className="border-t p-6 bg-background">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {editMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>{editMode ? 'Update Candidate' : 'Create Candidate'}</>
                )}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog >
  );
};

export default AddCandidateModal;
